import { isUndefined } from 'underscore';
import Promise from 'bluebird';
import React, { useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { withTheme } from 'react-jss';
import { withProps } from '@bingads-webui-react/with-props';
import { withSiteMap, getRoutePathById } from '@bingads-webui-campaign-react/with-site-map';
import { withCreationHeader } from '@bingads-webui-campaign-react/campaign-adgroup-creation';
import { ProgressiveLoadingBlock } from '@bingads-webui-campaign-react/shell-ui-common';
import config from '@bingads-webui-component-legacy/config/index';
import localStorage from '@bingads-webui-component-legacy/storage/localStorage';
import { StepWizard } from '@bingads-webui-campaign-react/step-wizard';
import { PERF_MAX_CAMPAIGN_CRUD_SCENARIOS } from '../common/perf-max-campaign-crud-scenarios';

const getCampaignObjective = () => {
  let campaignObjective = config.get('CampaignObjective');
  if (isUndefined(campaignObjective)) {
    if (localStorage.supportsStorage()) {
      campaignObjective = localStorage.get('CampaignObjective');
      config.set('CampaignObjective', campaignObjective);
    }
  }

  return campaignObjective;
};

const StepWizardWithDynamicOptions = ({
  dynamicOptions, campaignObjective, routeToNavigateToWhenDone, ...restProps
}) => {
  const extraStepsData = useMemo(() => ({ campaignObjective, ...dynamicOptions }), [campaignObjective, dynamicOptions]);
  return (
    <StepWizard
      wizardScenario={PERF_MAX_CAMPAIGN_CRUD_SCENARIOS.PERF_MAX_CAMPAIGN_CREATION}
      routeToNavigateToWhenDone={routeToNavigateToWhenDone}
      extraStepsData={extraStepsData}
      {...restProps}
    />
  );
};

StepWizardWithDynamicOptions.propTypes = {
  dynamicOptions: PropTypes.shape({}).isRequired,
  campaignObjective: PropTypes.string.isRequired,
  routeToNavigateToWhenDone: PropTypes.string.isRequired,
};

const PerfMaxCampaignCreationPageImpl = ((props) => {
  const campaignObjective = useMemo(() => getCampaignObjective(), []);
  const routeToNavigateToWhenDone = useMemo(() => getRoutePathById(props.siteMap, 'campaigns'), [props.siteMap]);

  const {
    adExtensionsDynamicFeaturesLoader, permissions, theme, scope, instrumentation, i18n,
  } = props;
  const dynamicOptionsLoader = useCallback(
    () => Promise.props({
      adExtensionsDynamicFeatures: adExtensionsDynamicFeaturesLoader({
        permissions,
        theme,
        scope,
        instrumentation,
        i18n,
      }),
      adExtensionsAssociationsFactory:
        import(/* webpackChunkName: "perf-max-campaign-creation" */'@bingads-webui-campaign-react/ad-extensions-associations-factory')
          .then(({ extensionsAssociationsFactory, getObjectiveExtensions }) => ({ extensionsAssociationsFactory, getObjectiveExtensions })),
    })
      .then(({ adExtensionsDynamicFeatures, adExtensionsAssociationsFactory }) => ({
        adExtensionsDynamicFeatures,
        adExtensionsAssociationsFactory,
      }))
      .then(dynamicOptions => ({
        default: withProps({ dynamicOptions }, StepWizardWithDynamicOptions),
      })),
    [adExtensionsDynamicFeaturesLoader, permissions, theme, scope, instrumentation, i18n]
  );

  const LazyView = useMemo(() => React.lazy(dynamicOptionsLoader), [dynamicOptionsLoader]);

  useEffect(() => {
    if (!campaignObjective) {
      props.scope.save('/campaign/vnext/createcampaign/objective');
    }

    return () => {
      // eslint-disable-next-line no-console
      console.log('[DWD] PerfMaxCampaignCreationPage unmounted');
      // clear objective choice in local storage
      if (localStorage.supportsStorage()) {
        localStorage.invalidate('CampaignObjective');
      }
    };
  }, [campaignObjective, props.scope]);

  return (
    <>
      {campaignObjective &&
        <React.Suspense fallback={<ProgressiveLoadingBlock />}>
          <LazyView routeToNavigateToWhenDone={routeToNavigateToWhenDone} campaignObjective={campaignObjective} {...props} />
        </React.Suspense>
      }
      {!campaignObjective &&
        <div data-waiting-redirect="due to no campaign objective" />
      }
    </>
  );
});

PerfMaxCampaignCreationPageImpl.propTypes = {
  siteMap: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  permissions: PropTypes.shape({}).isRequired,
  theme: PropTypes.shape({}).isRequired,
  scope: PropTypes.shape({
    save: PropTypes.func.isRequired,
  }).isRequired,
  instrumentation: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({}).isRequired,
  adExtensionsDynamicFeaturesLoader: PropTypes.func.isRequired,
};

export const PerfMaxCampaignCreationPage = withSiteMap(withCreationHeader(withTheme(PerfMaxCampaignCreationPageImpl, undefined, undefined, false, true)));
